import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from '../routes/ids.js';
const tk = (value) => value;
const fieldId = (value) => value;
export const MARKET_PRICE_CREATE_COMMAND_ID = 'cmd.data.marketPrices.create';
export const MARKET_PRICE_CORRECT_COMMAND_ID = 'cmd.data.marketPrices.correct';
function metadata(id) {
    return {
        id: id,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_PRICES,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: `experience.marketPrices.${id}`,
        accessibilityLabelKey: tk('card.marketPrices.title'),
        availability: 'AVAILABLE',
        owner: 'market-data',
    };
}
function requiredField(name, fieldType = 'text') {
    return {
        id: fieldId(`field.marketPrice.${name}`),
        name,
        labelKey: tk(`data:marketPrices.fields.${name}`),
        fieldType,
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        validation: {
            rules: [{ ruleId: 'required', messageKey: tk('validation.required') }],
            validateWhen: 'submit',
        },
        layout: { md: 6 },
    };
}
function optionalField(name, fieldType = 'text', helpTextKey) {
    return {
        id: fieldId(`field.marketPrice.${name}`),
        name,
        labelKey: tk(`data:marketPrices.fields.${name}`),
        fieldType,
        ...(helpTextKey ? { helpTextKey: tk(helpTextKey) } : {}),
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        layout: { md: 6 },
    };
}
const VALUATION_PRICE_TYPES = [
    ['CLOSE', 'data:marketPrices.priceType.CLOSE'],
    ['LAST', 'data:marketPrices.priceType.LAST'],
    ['NAV', 'data:marketPrices.priceType.NAV'],
    ['OPEN', 'data:marketPrices.priceType.OPEN'],
    ['HIGH', 'data:marketPrices.priceType.HIGH'],
    ['LOW', 'data:marketPrices.priceType.LOW'],
    ['BID', 'data:marketPrices.priceType.BID'],
    ['OFFER', 'data:marketPrices.priceType.OFFER'],
];
const CORRECTION_PRICE_TYPES = [
    ['CLOSE', 'data:marketPrices.priceType.CLOSE'],
    ['LAST', 'data:marketPrices.priceType.LAST'],
    ['NAV', 'data:marketPrices.priceType.NAV'],
    ['OPEN', 'data:marketPrices.priceType.OPEN'],
    ['HIGH', 'data:marketPrices.priceType.HIGH'],
    ['LOW', 'data:marketPrices.priceType.LOW'],
    ['VOLUME', 'data:marketPrices.priceType.VOLUME'],
    ['TURNOVER', 'data:marketPrices.priceType.TURNOVER'],
    ['TRADE_COUNT', 'data:marketPrices.priceType.TRADE_COUNT'],
    ['EVALUATED', 'data:marketPrices.priceType.EVALUATED'],
    ['MANUAL', 'data:marketPrices.priceType.MANUAL'],
    ['BID', 'data:marketPrices.priceType.BID'],
    ['OFFER', 'data:marketPrices.priceType.OFFER'],
];
function priceTypeField(required, correction = false) {
    const base = required ? requiredField('priceType', 'select') : optionalField('priceType', 'select');
    const options = correction ? CORRECTION_PRICE_TYPES : VALUATION_PRICE_TYPES;
    return {
        ...base,
        options: options.map(([value, labelKey]) => ({ value, labelKey: tk(labelKey) })),
        helpTextKey: tk(correction ? 'data:marketPrices.help.correctionPriceType' : 'data:marketPrices.help.priceType'),
    };
}
const instrumentField = {
    ...requiredField('instrumentSymbol', 'select'),
    optionSource: {
        adapterId: 'api',
        operationId: OPERATION_IDS.INSTRUMENT_LIST,
        valuePath: 'symbol',
        labelPath: 'instrumentName',
        descriptionPath: 'symbol',
        allowSearch: true,
        minimumSearchLength: 0,
    },
    helpTextKey: tk('data:marketPrices.help.instrument'),
};
const positivePriceRules = [
    { ruleId: 'min', params: { min: 0.00000001 }, messageKey: tk('validation.min') },
];
const priceField = {
    ...optionalField('price', 'decimal', 'data:marketPrices.help.price'),
    validation: {
        rules: positivePriceRules,
        validateWhen: 'submit',
    },
};
function marketPriceMetricField(name) {
    return {
        ...optionalField(name, 'decimal', `data:marketPrices.help.${name}`),
        validation: { rules: positivePriceRules, validateWhen: 'submit' },
    };
}
const activityMetricFields = [
    optionalField('volume', 'quantity', 'data:marketPrices.help.volume'),
    optionalField('turnover', 'money', 'data:marketPrices.help.turnover'),
    optionalField('tradeCount', 'number', 'data:marketPrices.help.tradeCount'),
];
const manualSourceField = {
    ...optionalField('source', 'text', 'data:marketPrices.help.source'),
    value: {
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'include',
    },
};
function saveAction(id, operationId, commandId, labelKey) {
    return {
        ...metadata(id),
        id: id,
        operationId: operationId,
        labelKey: tk(labelKey),
        iconKey: 'save',
        kind: 'command',
        commandId,
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: true,
            requiresValid: true,
            resetBehavior: 'toServerValues',
        },
        intent: 'primary',
        idempotencyRequired: true,
        successBehavior: 'NAVIGATE_BACK',
        successMessageKey: tk('common.saveSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        order: 10,
        group: 'primary',
    };
}
const createFields = [
    instrumentField,
    requiredField('tradedDate', 'date'),
    manualSourceField,
    marketPriceMetricField('open'),
    marketPriceMetricField('high'),
    marketPriceMetricField('low'),
    marketPriceMetricField('close'),
    marketPriceMetricField('last'),
    ...activityMetricFields,
    priceTypeField(false),
    priceField,
];
const correctionReasonField = {
    ...requiredField('reason', 'textarea'),
    helpTextKey: tk('data:marketPrices.help.correctionReason'),
    validation: {
        rules: [
            { ruleId: 'required', messageKey: tk('validation.required') },
            { ruleId: 'minLength', params: { min: 10 }, messageKey: tk('validation.minLength') },
            { ruleId: 'maxLength', params: { max: 500 }, messageKey: tk('validation.maxLength') },
        ],
        validateWhen: 'submit',
    },
    layout: { md: 12 },
};
const correctionFields = [
    {
        ...optionalField('instrumentSymbol'),
        value: {
            hiddenValuePolicy: 'preserve',
            submitBehavior: 'exclude',
            outputOnly: true,
        },
    },
    priceTypeField(false, true),
    {
        ...optionalField('price', 'decimal', 'data:marketPrices.help.correctionPrice'),
        validation: { rules: positivePriceRules, validateWhen: 'submit' },
    },
    optionalField('tradedDate', 'date'),
    optionalField('source'),
    correctionReasonField,
    {
        ...requiredField('expectedVersion', 'hidden'),
        value: {
            hiddenValuePolicy: 'preserve',
            submitBehavior: 'include',
            retainWhenHidden: true,
        },
    },
];
const viewFields = [
    optionalField('instrumentSymbol'),
    optionalField('instrumentName'),
    optionalField('tradedDate', 'date'),
    optionalField('observationKind'),
    optionalField('open', 'decimal'),
    optionalField('high', 'decimal'),
    optionalField('low', 'decimal'),
    optionalField('close', 'decimal'),
    optionalField('last', 'decimal'),
    optionalField('volume', 'quantity'),
    optionalField('turnover', 'money'),
    optionalField('tradeCount', 'number'),
    priceTypeField(false, true),
    optionalField('price', 'decimal'),
    optionalField('exchange'),
    optionalField('source'),
    optionalField('freshness'),
    optionalField('createdAt', 'datetime'),
    optionalField('updatedAt', 'datetime'),
].map((field) => ({
    ...field,
    value: {
        ...field.value,
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'exclude',
        outputOnly: true,
    },
}));
const createSections = [
    {
        id: 'section.marketPrice.scope',
        titleKey: tk('data:marketPrices.section.scope'),
        descriptionKey: tk('data:marketPrices.section.scopeDescription'),
        fields: createFields
            .filter((field) => ['instrumentSymbol', 'tradedDate', 'source'].includes(field.name))
            .map((field) => field.id),
        layout: { columns: 2, gap: 16 },
    },
    {
        id: 'section.marketPrice.marketSnapshot',
        titleKey: tk('data:marketPrices.section.marketSnapshot'),
        descriptionKey: tk('data:marketPrices.section.marketSnapshotDescription'),
        fields: createFields
            .filter((field) => ['open', 'high', 'low', 'close', 'last', 'volume', 'turnover', 'tradeCount'].includes(field.name))
            .map((field) => field.id),
        layout: { columns: 2, gap: 16 },
    },
    {
        id: 'section.marketPrice.valuation',
        titleKey: tk('data:marketPrices.section.valuation'),
        descriptionKey: tk('data:marketPrices.section.valuationDescription'),
        fields: createFields.filter((field) => ['priceType', 'price'].includes(field.name)).map((field) => field.id),
        layout: { columns: 2, gap: 16 },
    },
];
const correctionSections = (fields) => [
    {
        id: 'section.marketPrice.observation',
        titleKey: tk('data:marketPrices.section.observation'),
        descriptionKey: tk('data:marketPrices.section.observationDescription'),
        fields: fields.filter((field) => field.name !== 'reason' && field.name !== 'expectedVersion').map((field) => field.id),
        layout: { columns: 2, gap: 16 },
    },
    ...(fields.some((field) => field.name === 'reason')
        ? [{
                id: 'section.marketPrice.correction',
                titleKey: tk('data:marketPrices.section.correction'),
                descriptionKey: tk('data:marketPrices.section.correctionDescription'),
                fields: fields.filter((field) => field.name === 'reason' || field.name === 'expectedVersion').map((field) => field.id),
                layout: { columns: 1, gap: 16 },
            }]
        : []),
];
export const MARKET_PRICE_CREATE_FORM = {
    ...metadata('form.data.marketPrice.create'),
    titleKey: tk('data:marketPrices.createTitle'),
    descriptionKey: tk('data:marketPrices.createDescription'),
    mode: 'create',
    operationId: OPERATION_IDS.MARKET_DATA_CREATE,
    fields: createFields,
    sections: createSections,
    actions: [
        saveAction('action.marketPrice.create', OPERATION_IDS.MARKET_DATA_CREATE, MARKET_PRICE_CREATE_COMMAND_ID, 'data:marketPrices.actions.add'),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: MARKET_PRICE_CREATE_COMMAND_ID,
        successMessageKey: tk('data:marketPrices.createSuccess'),
        errorMessageKey: tk('common.error.unexpected'),
    },
};
export const MARKET_PRICE_EDIT_FORM = {
    ...metadata('form.data.marketPrice.edit'),
    titleKey: tk('data:marketPrices.editTitle'),
    descriptionKey: tk('data:marketPrices.editDescription'),
    mode: 'edit',
    operationId: OPERATION_IDS.MARKET_DATA_UPDATE,
    fields: correctionFields,
    sections: correctionSections(correctionFields),
    actions: [
        saveAction('action.marketPrice.correct', OPERATION_IDS.MARKET_DATA_UPDATE, MARKET_PRICE_CORRECT_COMMAND_ID, 'data:marketPrices.actions.recordCorrection'),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: MARKET_PRICE_CORRECT_COMMAND_ID,
        successMessageKey: tk('data:marketPrices.correctSuccess'),
        errorMessageKey: tk('common.error.unexpected'),
    },
};
export const MARKET_PRICE_VIEW_FORM = {
    ...metadata('form.data.marketPrice.view'),
    titleKey: tk('data:marketPrices.detailTitle'),
    descriptionKey: tk('data:marketPrices.detailDescription'),
    mode: 'view',
    operationId: OPERATION_IDS.MARKET_DATA_GET,
    fields: viewFields,
    sections: correctionSections(viewFields),
    actions: [],
    submission: {},
};
export const MARKET_PRICE_FORM_REGISTRY = Object.freeze({
    [String(MARKET_PRICE_CREATE_FORM.id)]: MARKET_PRICE_CREATE_FORM,
    [String(MARKET_PRICE_EDIT_FORM.id)]: MARKET_PRICE_EDIT_FORM,
    [String(MARKET_PRICE_VIEW_FORM.id)]: MARKET_PRICE_VIEW_FORM,
});
